import type { ClaimSet, PlayerState, Tombstones, TripState } from './types';

/**
 * Conflict-free merge of two divergent copies of the same player's state.
 *
 * Both ledgers are LWW-element-sets: a plate is claimed or undone, and whichever
 * of those two events carries the later timestamp wins. That makes sync
 * commutative and idempotent, so two phones that both spent an hour offline in a
 * blackspot merge without losing a claim from either. Plain last-write-wins on
 * the whole blob would silently discard one device's finds.
 *
 * Only genuinely scalar fields (display name, which trip is active) fall back to
 * last-write-wins, decided by the state's monotonic `version`.
 */

interface MergedSet {
  claims: ClaimSet;
  tombstones: Tombstones;
}

function maxMerge(a: Record<string, number>, b: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = { ...a };
  for (const [key, value] of Object.entries(b)) {
    const existing = out[key];
    if (existing === undefined || value > existing) out[key] = value;
  }
  return out;
}

export function mergeClaimSets(
  aClaims: ClaimSet, aTombs: Tombstones,
  bClaims: ClaimSet, bTombs: Tombstones,
): MergedSet {
  const claimTimes = maxMerge(aClaims, bClaims);
  const tombTimes = maxMerge(aTombs, bTombs);

  const claims: ClaimSet = {};
  const tombstones: Tombstones = {};

  const keys = new Set([...Object.keys(claimTimes), ...Object.keys(tombTimes)]);
  for (const key of keys) {
    const claimedAt = claimTimes[key];
    const undoneAt = tombTimes[key];

    if (claimedAt !== undefined && (undoneAt === undefined || claimedAt > undoneAt)) {
      // The claim wins, so the tombstone it beat can be dropped: any copy that
      // still holds that tombstone will lose to this claim again on re-merge.
      claims[key] = claimedAt;
    } else if (undoneAt !== undefined) {
      tombstones[key] = undoneAt;
    }
  }

  return { claims, tombstones };
}

function mergeTrips(a: TripState, b: TripState): TripState {
  const merged = mergeClaimSets(a.myClaims, a.undone, b.myClaims, b.undone);
  const newer = b.version > a.version ? b : a;
  const freshestBoard = b.leaderboardAt > a.leaderboardAt ? b : a;

  return {
    ...newer,
    myClaims: merged.claims,
    undone: merged.tombstones,
    // A trip that has ended stays ended, whichever copy saw it first.
    endedAt: a.endedAt ?? b.endedAt,
    leaderboard: freshestBoard.leaderboard,
    leaderboardAt: freshestBoard.leaderboardAt,
    version: Math.max(a.version, b.version),
  };
}

export function mergeStates(local: PlayerState, remote: PlayerState): PlayerState {
  const lifetime = mergeClaimSets(
    local.lifetime, local.lifetimeUndone,
    remote.lifetime, remote.lifetimeUndone,
  );

  const byId = new Map<string, TripState>();
  for (const trip of local.trips) byId.set(trip.id, trip);
  for (const trip of remote.trips) {
    const existing = byId.get(trip.id);
    byId.set(trip.id, existing ? mergeTrips(existing, trip) : trip);
  }

  const newer = remote.version > local.version ? remote : local;

  return {
    schemaVersion: Math.max(local.schemaVersion, remote.schemaVersion),
    playerId: local.playerId,
    displayName: newer.displayName,
    lifetime: lifetime.claims,
    lifetimeUndone: lifetime.tombstones,
    trips: [...byId.values()].sort((x, y) => y.startedAt - x.startedAt),
    activeTripId: newer.activeTripId,
    version: Math.max(local.version, remote.version) + 1,
  };
}
